import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AppRole =
  | "systeemadmin"
  | "beheerder"
  | "klant"
  | "gebruiker"
  | "ontvanger"
  | "production"
  | "accountmanager"
  | "marketing";

interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
}

export const useUserRole = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async (): Promise<UserRole | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", user.id);

      if (error) {
        return null;
      }

      if (!data || data.length === 0) return null;

      // If user has multiple roles, pick the highest-priority one
      const priority: Record<string, number> = {
        systeemadmin: 0,
        beheerder: 1,
        accountmanager: 2,
        marketing: 3,
        gebruiker: 4,
        production: 5,
        klant: 6,
        ontvanger: 7,
      };
      const sorted = [...data].sort((a, b) => (priority[a.role] ?? 99) - (priority[b.role] ?? 99));
      const best = sorted[0];

      return {
        ...best,
        role: best.role as AppRole,
      };
    },
    enabled: !!user,
  });
};

export const useIsAdmin = () => {
  const { data: userRole, isLoading } = useUserRole();
  
  const role = userRole?.role;
  return {
    // Marketing has read-only access to the admin portal (DB-level RLS enforces no writes).
    isAdmin:
      role === "systeemadmin" ||
      role === "beheerder" ||
      role === "accountmanager" ||
      role === "marketing",
    isLoading,
  };
};

/**
 * Returns true if the current user has a read-only role (e.g. marketing).
 * Use to disable buttons/forms in the UI. DB-level RLS is the source of truth.
 */
export const useIsReadOnly = () => {
  const { data: userRole, isLoading } = useUserRole();
  return {
    isReadOnly: userRole?.role === "marketing",
    isLoading,
  };
};
